"use client";

import { useMemo } from "react";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  findJargon,
  longSentences,
  parseJargonPairs,
} from "@/lib/tool-kit/research-workflow/plain-language";

/*
 * Review panels the research-workflow generators share: a checklist of required
 * elements with a status beside each, and the plain-language check that reads what the
 * researcher typed. Every word comes from the calling widget's strings through props.
 */

export type CheckStatus = "covered" | "missing" | "standard" | "na";

const BADGE: Record<CheckStatus, string> = {
  covered:
    "shrink-0 rounded-md bg-green-600/10 px-2 py-0.5 font-medium text-green-700 text-xs dark:text-green-400",
  missing:
    "shrink-0 rounded-md bg-amber-500/10 px-2 py-0.5 font-medium text-amber-700 text-xs dark:text-amber-400",
  standard:
    "shrink-0 rounded-md bg-primary/10 px-2 py-0.5 font-medium text-primary text-xs",
  na: "shrink-0 rounded-md bg-muted px-2 py-0.5 font-medium text-muted-foreground text-xs",
};

export function StatusList({
  items,
  labels,
}: {
  items: readonly { id: string; label: string; status: CheckStatus }[];
  labels: Record<CheckStatus, string>;
}) {
  return (
    <ul className="divide-y divide-border rounded-lg border border-border bg-card">
      {items.map((item) => (
        <li
          className="flex items-center justify-between gap-4 px-3 py-2 text-sm"
          key={item.id}
        >
          <span className="text-foreground">{item.label}</span>
          <span className={BADGE[item.status]}>{labels[item.status]}</span>
        </li>
      ))}
    </ul>
  );
}

export interface PlainLanguageLabels {
  /** Plural message with `{count}`. */
  longCount: string;
  /** Plural message with `{start}` and `{count}`. */
  longItem: string;
  jargonLead: string;
  /** With `{term}` and `{plain}`. */
  jargonItem: string;
  plainClear: string;
}

/** Long sentences and jargon in `text`, with a plainer word beside each term. */
export function PlainLanguagePanel({
  heading,
  text,
  jargon,
  labels,
  locale,
  extraWarning,
}: {
  heading: string;
  text: string;
  /** `"utilize = use"` lines from the widget's strings. */
  jargon: readonly string[];
  labels: PlainLanguageLabels;
  locale: string;
  /** A widget-specific caution shown above the findings, or null. */
  extraWarning: string | null;
}) {
  const fmt = useToolNumber();
  const pairs = useMemo(() => parseJargonPairs(jargon), [jargon]);
  const hits = useMemo(() => findJargon(text, pairs), [text, pairs]);
  const long = useMemo(() => longSentences(text, 25, locale), [text, locale]);
  const clear = hits.length === 0 && long.length === 0 && !extraWarning;

  return (
    <section aria-live="polite" className="space-y-3">
      <h2 className={WIDGET_H2}>{heading}</h2>
      {extraWarning ? (
        <p className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-4 text-foreground text-sm">
          {extraWarning}
        </p>
      ) : null}
      {clear ? (
        <p className="text-muted-foreground text-sm">{labels.plainClear}</p>
      ) : null}
      {long.length > 0 ? (
        <div className="space-y-1 text-sm">
          <p className="text-foreground">
            {tPlural(labels.longCount, locale, long.length, {
              count: fmt(long.length),
            })}
          </p>
          <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
            {long.map((sentence) => (
              <li key={`${sentence.start}-${sentence.words}`}>
                {tPlural(labels.longItem, locale, sentence.words, {
                  start: sentence.start,
                  count: fmt(sentence.words),
                })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hits.length > 0 ? (
        <div className="space-y-1 text-sm">
          <p className="text-foreground">{labels.jargonLead}</p>
          <ul className="list-disc space-y-1 pl-6 text-muted-foreground">
            {hits.map((hit) => (
              <li key={hit.term}>
                {t(labels.jargonItem, { term: hit.term, plain: hit.plain })}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
